import React, { Component } from "react";
import classnames from "classnames";
import { observable } from "mobx";
import { observer } from "mobx-react";

import { defined } from "@formant/core/common/defined";
import { IDevice } from "@formant/core/model/IDevice";
import { Uuid } from "@formant/core/model/Uuid";

import { onlineDevicesStore, routerStore } from "../../../stores";
import { DataTable } from "../../common/DataTable";
import { IDataTableColumn } from "../../common/DataTable/IDataTableColumn";
import { Pagination } from "../../common/Pagination";
import { Scrollable } from "../../common/Scrollable";
import { Tags } from "../../common/Tags";
import { DeviceStatus } from "../../DeviceStatus";
import { RowDate } from "../RowDate";
import { RowTitle } from "../RowTitle";
import { Actions } from "./Actions";
import { DeviceCheck } from "./DeviceCheck";
import * as styles from "./DevicesTable.scss";
import { ReprovisionPopup } from "./ReprovisionPopup";
import { RowCheck } from "./RowCheck";

export interface IDevicesTableProps {
    devices: IDevice[];
    allDevices: IDevice[];
    selectMode: boolean;
    selectedIds: Uuid[];
    page: number;
    perPage: number;
    count?: number;
    onUpdateSelected: (selectedIds: Uuid[]) => void;
    onChangePage: (page: number) => void;
}

@observer
export class DevicesTable extends Component<IDevicesTableProps> {
    private devices: IDevice[] = [];
    private allDeviceIds: Uuid[] = [];

    @observable
    private reprovisionDevice: IDevice | undefined;

    public render() {
        const {
            devices,
            selectMode,
            selectedIds,
            page,
            perPage,
            count,
            onChangePage,
        } = this.props;

        const loading = devices === undefined || count === undefined;
        this.devices = devices || this.devices;

        const online = onlineDevicesStore.online();

        const columns: IDataTableColumn<IDevice>[] = [
            {
                header: (
                    <>
                        <DeviceCheck
                            checked={selectedIds.length > 0}
                            ambiguous={
                                selectedIds.length > 0 &&
                                selectedIds.length !== this.allDeviceIds.length
                            }
                            visible={selectMode}
                            className={styles.check}
                            onToggle={this.onToggleAll}
                        />
                        Device
                    </>
                ),
                className: styles.device,
                row: _ => (
                    <>
                        <RowCheck
                            device={_}
                            checked={selectedIds.includes(defined(_.id))}
                            visible={selectMode}
                            onToggle={this.onToggle}
                        />
                        <RowTitle
                            title={_.name}
                            icon="device"
                            className={styles.title}
                        />
                        <DeviceStatus
                            id={defined(_.id)}
                            online={online?.has(defined(_.id)) || false}
                        />
                    </>
                ),
            },
            {
                header: "Tags",
                row: _ => <Tags tags={_.tags} />,
                className: styles.tags,
                hideBelow: "md",
            },
            {
                header: "Desired",
                row: _ => `v${_.desiredConfigurationVersion || 0}`,
                className: styles.desired,
                hideBelow: "sm",
            },
            {
                header: "Active",
                row: _ => `v${_.state?.reportedConfiguration?.version || 0}`,
                className: styles.active,
            },
            {
                header: "Created",
                row: _ => <RowDate date={_.createdAt} />,
                className: styles.created,
                hideBelow: "lg",
            },
            {
                header: "Updated",
                row: _ => <RowDate date={_.updatedAt} />,
                className: styles.updated,
                hideBelow: "lg",
            },
            {
                header: "",
                row: _ => (
                    <Actions device={_} onReprovision={this.onReprovision} />
                ),
            },
        ];

        return (
            <div
                className={classnames(
                    styles["devices-table"],
                    selectMode && styles["select-mode"],
                )}
            >
                <ReprovisionPopup
                    device={this.reprovisionDevice}
                    onClose={this.onCloseReprovision}
                />
                <Scrollable>
                    <DataTable
                        rows={this.devices}
                        columns={columns}
                        loading={loading}
                        noResultsText="No devices found"
                        onRowClicked={this.onDeviceClicked}
                        className={styles.table}
                        rowTestId={_ => `devices-settings-${_.id}`}
                    />
                </Scrollable>
                {count !== undefined && (
                    <Pagination
                        page={page}
                        total={count}
                        perPage={perPage}
                        onChange={onChangePage}
                        className={styles.pagination}
                    />
                )}
            </div>
        );
    }

    private onToggle = (id: Uuid) => {
        const { selectedIds, onUpdateSelected } = this.props;
        if (selectedIds.some(_ => _ === id)) {
            onUpdateSelected(selectedIds.filter(_ => _ !== id));
        } else {
            onUpdateSelected([...selectedIds, id]);
        }
    };

    private onToggleAll = async () => {
        const { selectedIds, onUpdateSelected, allDevices } = this.props;

        const deviceIds = allDevices.map(_ => defined(_.id));
        this.allDeviceIds = deviceIds;

        if (selectedIds.length > 0) {
            onUpdateSelected([]);
        } else {
            onUpdateSelected(deviceIds);
        }
    };

    private onReprovision = (device: IDevice) => {
        this.reprovisionDevice = device;
    };

    private onCloseReprovision = () => {
        this.reprovisionDevice = undefined;
    };

    private onDeviceClicked = async (device: IDevice) => {
        const { navigate, routeTo } = routerStore;
        await navigate(routeTo.editDevice({ id: defined(device.id) }));
    };
}
